import React, {useMemo} from 'react';

import type {WorkflowDefinitionActionResponse} from 'src/api/ProactApiClient';
import {NotificationRuleCourseChangeTypeEnum} from 'src/api/ProactApiClient';
import {Description} from 'src/design/atoms/Description';
import {NotificationAttributeGrid} from 'src/app/elements/NotificationAttributeGrid';
import {useGetNotificationRuleVariablesQuery} from 'src/redux/queries/notificationRule';
import {computePhaseVariables} from './phaseVariables';
import type {IWorkflowDesignerMetadata} from './types';
import {getCommonVariablesForWorkflow} from './workflowHelpers';

export interface WorkflowNotificationAttributeSectionProps {
  action: WorkflowDefinitionActionResponse;
  metadata: IWorkflowDesignerMetadata;
}
export const WorkflowNotificationAttributeSection: React.FC<
  WorkflowNotificationAttributeSectionProps
> = ({action, metadata}) => {
  const {
    ruleType,
    module,
    isScheduled,
    isSingleTriggerWorkflow,
    primaryTrigger,
    allPhases,
    currentPhaseIndex,
  } = metadata;
  const {data: variablesResp} = useGetNotificationRuleVariablesQuery(
    {
      ruleType: ruleType,
      module: module,
      formDefinitionId: primaryTrigger?.triggerFormDefinitionId,
      courseChangeType:
        NotificationRuleCourseChangeTypeEnum.CourseAssignmentChange,
    },
    {
      skip: !isSingleTriggerWorkflow,
    }
  );
  const variables = isSingleTriggerWorkflow
    ? variablesResp?.variables ?? []
    : getCommonVariablesForWorkflow(action, isScheduled);

  const phaseVariableGroups = useMemo(
    () => computePhaseVariables(allPhases, currentPhaseIndex),
    [allPhases, currentPhaseIndex]
  );

  return (
    <>
      <NotificationAttributeGrid
        variables={variables}
        allowCopyToClipboard={true}
        phaseVariableGroups={phaseVariableGroups}
      />
      {!isSingleTriggerWorkflow && (
        <Description text="No variables found. Module specific variables are only available when a single trigger is defined. To see module specific variables, define a single trigger and select a module." />
      )}
    </>
  );
};
